import jsPDF from "jspdf";
import { heicTo } from "heic-to";

export type FileType = "IMAGE" | "PDF" | "HEIC" | "HEIF";

type DetectedFormat =
  | "JPEG"
  | "PNG"
  | "GIF"
  | "WEBP"
  | "BMP"
  | "TIFF"
  | "HEIC"
  | "AVIF"
  | "PDF"
  | "UNKNOWN";

export interface PdfOptions {
  margin?: number;
  quality?: number;
  background?: string;
  orientation?: "portrait" | "landscape";
  format?: string | number[];
}

const MAX_CANVAS_PIXELS = 12_000_000;
const MAX_CANVAS_DIMENSION = 6000;

const HEIF_BRANDS = new Set([
  "heic",
  "heix",
  "heim",
  "heis",
  "hevc",
  "hevx",
  "hevm",
  "hevs",
  "mif1",
  "msf1",
]);

const AVIF_BRANDS = new Set(["avif", "avis"]);

export const cleanBase64 = (base64Data: string): string => {
  return base64Data
    .replace(/^data:[^;]+;base64,/i, "")
    .replace(/\s/g, "")
    .replace(/^["']|["']$/g, "")
    .replace(/-/g, "+")
    .replace(/_/g, "/");
};

const base64ToBytes = (base64Data: string): Uint8Array => {
  const base64 = cleanBase64(base64Data);

  if (!base64) {
    throw new Error("Empty file data");
  }

  let binary: string;

  try {
    binary = atob(base64);
  } catch {
    throw new Error("File data is not valid base64");
  }

  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};


const base64HeaderBytes = (base64Data: string, byteCount = 48): Uint8Array => {
  const base64 = cleanBase64(base64Data);
  const charCount = Math.ceil(byteCount / 3) * 4;
  const slice = base64.slice(0, charCount);
  const aligned = slice.slice(0, slice.length - (slice.length % 4));

  if (!aligned) {
    return new Uint8Array(0);
  }

  try {
    const binary = atob(aligned);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
  } catch {
    return new Uint8Array(0);
  }
};

const bytesToAscii = (bytes: Uint8Array, start: number, end: number): string => {
  let out = "";

  for (let i = start; i < end && i < bytes.length; i++) {
    out += String.fromCharCode(bytes[i]);
  }

  return out;
};

const bytesToBlob = (bytes: Uint8Array, mimeType: string): Blob =>
  new Blob([bytes], { type: mimeType });


export const detectFormat = (base64Data: string): DetectedFormat => {
  const b = base64HeaderBytes(base64Data);

  if (b.length < 12) {
    return "UNKNOWN";
  }

  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) {
    return "JPEG";
  }

  if (
    b[0] === 0x89 &&
    b[1] === 0x50 &&
    b[2] === 0x4e &&
    b[3] === 0x47 &&
    b[4] === 0x0d &&
    b[5] === 0x0a &&
    b[6] === 0x1a &&
    b[7] === 0x0a
  ) {
    return "PNG";
  }

  if (bytesToAscii(b, 0, 4) === "%PDF") {
    return "PDF";
  }

  if (bytesToAscii(b, 0, 3) === "GIF") {
    return "GIF";
  }

  if (bytesToAscii(b, 0, 4) === "RIFF" && bytesToAscii(b, 8, 12) === "WEBP") {
    return "WEBP";
  }

  if (b[0] === 0x42 && b[1] === 0x4d) {
    return "BMP";
  }

  if (
    (b[0] === 0x49 && b[1] === 0x49 && b[2] === 0x2a && b[3] === 0x00) ||
    (b[0] === 0x4d && b[1] === 0x4d && b[2] === 0x00 && b[3] === 0x2a)
  ) {
    return "TIFF";
  }

  if (bytesToAscii(b, 4, 8) === "ftyp") {
    const majorBrand = bytesToAscii(b, 8, 12).toLowerCase();

    if (AVIF_BRANDS.has(majorBrand)) {
      return "AVIF";
    }

    if (HEIF_BRANDS.has(majorBrand)) {
      return "HEIC";
    }

    for (let offset = 16; offset + 4 <= b.length; offset += 4) {
      const brand = bytesToAscii(b, offset, offset + 4).toLowerCase();

      if (AVIF_BRANDS.has(brand)) {
        return "AVIF";
      }

      if (HEIF_BRANDS.has(brand)) {
        return "HEIC";
      }
    }
  }

  return "UNKNOWN";
};

const mimeForFormat = (format: DetectedFormat): string => {
  switch (format) {
    case "JPEG":
      return "image/jpeg";
    case "PNG":
      return "image/png";
    case "GIF":
      return "image/gif";
    case "WEBP":
      return "image/webp";
    case "BMP":
      return "image/bmp";
    case "TIFF":
      return "image/tiff";
    case "HEIC":
      return "image/heic";
    case "AVIF":
      return "image/avif";
    case "PDF":
      return "application/pdf";
    default:
      return "application/octet-stream";
  }
};

const isHeifLike = (format: DetectedFormat, type?: FileType): boolean =>
  format === "HEIC" ||
  format === "AVIF" ||
  ((type === "HEIC" || type === "HEIF") && format === "UNKNOWN");


const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.decoding = "async";

    image.onload = () => {
      if (!image.naturalWidth || !image.naturalHeight) {
        reject(new Error("Image decoded with zero dimensions"));
        return;
      }

      resolve(image);
    };

    image.onerror = () => {
      reject(new Error("Browser could not decode this image format"));
    };

    image.src = src;
  });

const blobToBitmap = async (blob: Blob): Promise<ImageBitmap> => {
  try {
    return await createImageBitmap(blob, { imageOrientation: "from-image" as any });
  } catch {
    return await createImageBitmap(blob);
  }
};

const drawToCanvas = (
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  background: string,
): HTMLCanvasElement => {
  if (!sourceWidth || !sourceHeight) {
    throw new Error("Image has invalid dimensions");
  }

  let scale = 1;

  const pixels = sourceWidth * sourceHeight;

  if (pixels > MAX_CANVAS_PIXELS) {
    scale = Math.sqrt(MAX_CANVAS_PIXELS / pixels);
  }

  const longestEdge = Math.max(sourceWidth, sourceHeight) * scale;

  if (longestEdge > MAX_CANVAS_DIMENSION) {
    scale *= MAX_CANVAS_DIMENSION / longestEdge;
  }

  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement("canvas");

  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Could not create canvas context");
  }

  context.fillStyle = background;
  context.fillRect(0, 0, width, height);

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";

  context.drawImage(source, 0, 0, width, height);

  return canvas;
};

const decodeHeif = async (
  bytes: Uint8Array,
  mimeType: string,
): Promise<{ source: CanvasImageSource; width: number; height: number }> => {
  const blob = bytesToBlob(bytes, mimeType);

  
  try {
    const bitmap = await blobToBitmap(blob);

    if (bitmap.width && bitmap.height) {
      return { source: bitmap, width: bitmap.width, height: bitmap.height };
    }
  } catch {
   
  }


  try {
    const converted = await heicTo({
      blob,
      type: "image/jpeg",
      quality: 0.94,
    });

    const jpegBlob =
      converted instanceof Blob ? converted : new Blob([converted as BlobPart]);

    try {
      const bitmap = await blobToBitmap(jpegBlob);
      return { source: bitmap, width: bitmap.width, height: bitmap.height };
    } catch {
      const url = URL.createObjectURL(jpegBlob);

      try {
        const image = await loadImageElement(url);
        return {
          source: image,
          width: image.naturalWidth,
          height: image.naturalHeight,
        };
      } finally {
        URL.revokeObjectURL(url);
      }
    }
  } catch (err) {
    throw new Error(
      `HEIC/HEIF conversion failed: ${
        err instanceof Error ? err.message : "unknown error"
      }`,
    );
  }
};

const decodeStandardImage = async (
  bytes: Uint8Array,
  mimeType: string,
): Promise<{ source: CanvasImageSource; width: number; height: number }> => {
  const blob = bytesToBlob(bytes, mimeType);

  try {
    const bitmap = await blobToBitmap(blob);

    if (bitmap.width && bitmap.height) {
      return { source: bitmap, width: bitmap.width, height: bitmap.height };
    }
  } catch {
  }

  const url = URL.createObjectURL(blob);

  try {
    const image = await loadImageElement(url);
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
};


export const imageToCanvas = async (
  base64Data: string,
  type?: FileType,
  background = "#ffffff",
): Promise<HTMLCanvasElement> => {
  const detected = detectFormat(base64Data);

  if (detected === "PDF") {
    throw new Error("Payload is a PDF, not an image");
  }

  const bytes = base64ToBytes(base64Data);

  const heif = isHeifLike(detected, type);

  const mimeType = heif
    ? mimeForFormat(detected === "AVIF" ? "AVIF" : "HEIC")
    : mimeForFormat(detected === "UNKNOWN" ? "JPEG" : detected);

  let decoded;

  if (heif) {
    decoded = await decodeHeif(bytes, mimeType);
  } else {
    try {
      decoded = await decodeStandardImage(bytes, mimeType);
    } catch (err) {
      if (detected === "UNKNOWN") {
        decoded = await decodeHeif(bytes, "image/heic");
      } else {
        throw err;
      }
    }
  }

  const canvas = drawToCanvas(
    decoded.source,
    decoded.width,
    decoded.height,
    background,
  );

  if (typeof ImageBitmap !== "undefined" && decoded.source instanceof ImageBitmap) {
    decoded.source.close();
  }

  return canvas;
};

const getPdfDimensions = (
  imageWidth: number,
  imageHeight: number,
  doc: jsPDF,
  margin: number,
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const maxWidth = pageWidth - margin * 2;
  const maxHeight = pageHeight - margin * 2;

  // Contain, never upscale past the printable area.
  const ratio = Math.min(maxWidth / imageWidth, maxHeight / imageHeight);

  const width = imageWidth * ratio;
  const height = imageHeight * ratio;

  return {
    x: (pageWidth - width) / 2,
    y: (pageHeight - height) / 2,
    width,
    height,
  };
};

export const createImagePdf = async (
  base64Data: string,
  type?: FileType,
  options: PdfOptions = {},
): Promise<jsPDF> => {
  const {
    margin = 10,
    quality = 0.92,
    background = "#ffffff",
    orientation,
    format = "a4",
  } = options;

  const canvas = await imageToCanvas(base64Data, type, background);

  const isLandscape = canvas.width > canvas.height;

  const doc = new jsPDF({
    orientation: orientation ?? (isLandscape ? "landscape" : "portrait"),
    unit: "mm",
    format,
    compress: true,
  });

  const dimensions = getPdfDimensions(canvas.width, canvas.height, doc, margin);

  const dataUrl = canvas.toDataURL("image/jpeg", quality);

  doc.addImage(
    dataUrl,
    "JPEG",
    dimensions.x,
    dimensions.y,
    dimensions.width,
    dimensions.height,
    undefined,
    "FAST",
  );

 
  canvas.width = 0;
  canvas.height = 0;

  return doc;
};

export const base64ToPdfBlob = (base64Data: string): Blob =>
  bytesToBlob(base64ToBytes(base64Data), "application/pdf");


export const toPdfBlob = async (
  base64Data: string,
  type?: FileType,
  options?: PdfOptions,
): Promise<Blob> => {
  const detected = detectFormat(base64Data);

  if (type === "PDF" || detected === "PDF") {
    return base64ToPdfBlob(base64Data);
  }

  const doc = await createImagePdf(base64Data, type, options);

  return doc.output("blob");
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement("a");

  link.href = blobUrl;
  link.download = fileName;
  link.rel = "noopener";
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
};

const sanitiseFileName = (name: string): string => {
  const trimmed = (name || "document").replace(/[\\/:*?"<>|]/g, "_").trim();
  const withoutExt = trimmed.replace(/\.pdf$/i, "");

  return `${withoutExt || "document"}.pdf`;
};

export interface TransferOptions extends PdfOptions {
  onError?: (error: Error) => void;
  rethrow?: boolean;
}

export const handleDownloadPdf = async (
  base64Data: string,
  Name: string,
  type?: FileType,
  setIsDownloading?: (download: boolean) => void,
  options: TransferOptions = {},
) => {
  const { onError, rethrow, ...pdfOptions } = options;

  try {
    setIsDownloading?.(true);

    const blob = await toPdfBlob(base64Data, type, pdfOptions);

    triggerBlobDownload(blob, sanitiseFileName(Name));
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));

    console.error("PDF download error:", error);
    onError?.(error);

    if (rethrow) {
      throw error;
    }
  } finally {
    setIsDownloading?.(false);
  }
};


const printBlob = (blob: Blob): Promise<void> =>
  new Promise((resolve, reject) => {
    const blobUrl = URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");

    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "1px";
    iframe.style.height = "1px";
    iframe.style.border = "0";
    iframe.style.opacity = "0";
    iframe.setAttribute("aria-hidden", "true");

    let settled = false;

    const cleanup = () => {
      setTimeout(() => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }

        URL.revokeObjectURL(blobUrl);
      }, 1000);
    };

    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(message));
    };

    const succeed = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    iframe.onload = () => {
      try {
        const frameWindow = iframe.contentWindow;

        if (!frameWindow) {
          fail("Could not access the print frame");
          return;
        }

        frameWindow.focus();
        setTimeout(() => {
          try {
            frameWindow.print();
            succeed();
          } catch {
            fail("The browser blocked the print dialog");
          }
        }, 150);
      } catch {
        fail("The browser blocked the print dialog");
      }
    };

    iframe.onerror = () => fail("Failed to load the document for printing");

    setTimeout(() => {
      if (!settled) succeed();
    }, 8000);

    iframe.src = blobUrl;

    document.body.appendChild(iframe);
  });

export const handlePrint = async (
  base64Data: string,
  type?: FileType,
  setIsPrinting?: (printing: boolean) => void,
  options: TransferOptions = {},
) => {
  const { onError, rethrow, ...pdfOptions } = options;

  try {
    setIsPrinting?.(true);

    const detected = detectFormat(base64Data);

    let blob: Blob;

    if (type === "PDF" || detected === "PDF") {
      blob = base64ToPdfBlob(base64Data);
    } else {
      const doc = await createImagePdf(base64Data, type, pdfOptions);
      doc.autoPrint();
      blob = doc.output("blob");
    }

    await printBlob(blob);
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));

    console.error("PDF print error:", error);
    onError?.(error);

    if (rethrow) {
      throw error;
    }
  } finally {
    setIsPrinting?.(false);
  }
};

export function toSnakeCase(str: string): string {
  return str
    .replace(/([A-Z])/g, "_$1")
    .replace(/^_/, "")
    .toLowerCase();
}

export const getGridColumnClass = (ColumnSize: number) => {
  if (ColumnSize === 0 || ColumnSize === 1) {
    return "col-span-12";
  }

  if (ColumnSize === 2) {
    return "col-span-6";
  }

  if (ColumnSize === 3) {
    return "col-span-4";
  }

  if (ColumnSize === 4) {
    return "col-span-3";
  }

  return "col-span-12";
};